import React, { lazy } from "react";
import { Navigate } from "react-router-dom";
import ComponentLoader from "./component-loader";
import RequireRole from "./require-role";
import { ROLE } from "enums/user";
import AdminLayout from "layouts/admin/components/admin-layout";
import Team from "layouts/admin/team/team";
import Teams from "layouts/admin/team";
import Success from "layouts/admin/success";
import RestaurantModule from "layouts/admin/restaurant";
import RestaurantHub from "layouts/admin/restaurant/hub";
import ComingSoon from "layouts/admin/restaurant/coming-soon";
import restaurantModules from "layouts/admin/restaurant/modules";
// import Rebranding from 'layouts/admin/rebranding/rebranding'

// MAIN
const Admin = ComponentLoader(lazy(() => import("../layouts/admin/admin")));

// ACCOUNT
const Account = ComponentLoader(
  lazy(() => import("../layouts/common/account/account"))
);
const Profile = ComponentLoader(
  lazy(() => import("../layouts/common/account/profile"))
);
const UpdatePassword = ComponentLoader(
  lazy(() => import("../layouts/common/account/update-pass"))
);

// CARD-HOLDER
const CardHolders = ComponentLoader(
  lazy(() => import("../layouts/admin/cardholder"))
);
const CardHoldersList = ComponentLoader(
  lazy(() => import("../layouts/admin/cardholder/cardholder"))
);
const Bulkmanage = ComponentLoader(
  lazy(() => import("../layouts/admin/cardholder/bulkmanage"))
);
const ViewInsight = ComponentLoader(
  lazy(() => import("../layouts/admin/components/view-insight"))
);

// MANAGE DESIGN
const Designs = ComponentLoader(lazy(() => import("../layouts/admin/design")));
const CreateDesign = ComponentLoader(
  lazy(() => import("../layouts/admin/design/design-form"))
);
const DesignList = ComponentLoader(
  lazy(() => import("../layouts/admin/design/design-table"))
);

// BROADCAST
const BroadCastModule = ComponentLoader(
  lazy(() => import("../layouts/admin/broadcast"))
);
const BroadCast = ComponentLoader(
  lazy(() => import("../layouts/admin/broadcast/broadcast"))
);

const HelpVideos = ComponentLoader(
  lazy(() => import("../layouts/admin/help-videos"))
)

// SETTINGS
const OrganisationSettings = ComponentLoader(
  lazy(() => import("../layouts/admin/settings/settings"))
);
const InvoicesPage = ComponentLoader(
  lazy(() => import("../layouts/admin/settings/invoices"))
);

// SUBSCRIPTION PLANS
const SubscriptionPlans = ComponentLoader(
  lazy(() => import("../layouts/admin/subscriptionplan"))
);
const Payment = ComponentLoader(
  lazy(() => import("../layouts/admin/payment/payment"))
);
const Payfast = ComponentLoader(
  lazy(() => import("../layouts/admin/payfast/payfast"))
);

const Rebranding = ComponentLoader(
  lazy(() => import("../layouts/admin/rebranding/rebranding"))
);
const CampaignStudio = ComponentLoader(
  lazy(() => import("../layouts/admin/campaign-studio"))
);
const CampaignList = ComponentLoader(
  lazy(() => import("../layouts/admin/campaigns"))
);
const CampaignInsights = ComponentLoader(
  lazy(() => import("../layouts/admin/campaigns/insights"))
);
const CampaignMembers = ComponentLoader(
  lazy(() => import("../layouts/admin/campaigns/members"))
);
const ScanConsole = ComponentLoader(
  lazy(() => import("../layouts/admin/scan-console"))
);
const SuperadminCampaigns = ComponentLoader(
  lazy(() => import("../layouts/admin/superadmin-campaigns"))
);

// Dev-only demo of the shared shell building blocks; never registered in production bundles.
const UiKit =
  process.env.NODE_ENV !== "production"
    ? ComponentLoader(lazy(() => import("../layouts/admin/ui-kit")))
    : null;

// ORDER MANAGEMENT (restaurant)
const RestaurantProducts = ComponentLoader(
  lazy(() => import("../layouts/admin/restaurant/products/products"))
);
const RestaurantCategories = ComponentLoader(
  lazy(() => import("../layouts/admin/restaurant/categories/categories"))
);
const RestaurantBanners = ComponentLoader(
  lazy(() => import("../layouts/admin/restaurant/banners/banners"))
);
const RestaurantDeals = ComponentLoader(
  lazy(() => import("../layouts/admin/restaurant/deals/deals"))
);
const RestaurantOrders = ComponentLoader(
  lazy(() => import("../layouts/admin/restaurant/orders/orders"))
);
const RestaurantCustomers = ComponentLoader(
  lazy(() => import("../layouts/admin/restaurant/customers/customers"))
);
const RestaurantBranches = ComponentLoader(
  lazy(() => import("../layouts/admin/restaurant/branches/branches"))
);
const RestaurantAnalytics = ComponentLoader(
  lazy(() => import("../layouts/admin/restaurant/analytics/analytics"))
);
const RestaurantSettings = ComponentLoader(
  lazy(() => import("../layouts/admin/restaurant/settings/settings"))
);
const RestaurantSeo = ComponentLoader(
  lazy(() => import("../layouts/admin/restaurant/seo/seo"))
);

// Ported modules -> element. Anything not listed falls back to "coming soon".
const restaurantElements = {
  products: <RestaurantProducts />,
  categories: <RestaurantCategories />,
  banners: <RestaurantBanners />,
  deals: <RestaurantDeals />,
  orders: <RestaurantOrders />,
  customers: <RestaurantCustomers />,
  branches: <RestaurantBranches />,
  analytics: <RestaurantAnalytics />,
  settings: <RestaurantSettings />,
  seo: <RestaurantSeo />,
};

const restaurantChildren = [
  { path: "", index: true, element: <RestaurantHub /> },
  ...restaurantModules.map((module) => ({
    path: module.path,
    element: restaurantElements[module.path] || (
      <ComingSoon title={module.label} />
    ),
  })),
];
const PayByCard = ComponentLoader(
  lazy(() =>
    import(
      "../layouts/authentication/register/organisation/subscription/paybycard"
    )
  )
);

const ContactSales = ComponentLoader(
  lazy(() =>
    import(
      "../layouts/authentication/register/organisation/subscription/submiterequest"
    )
  )
);

const PaymentSuccess = ComponentLoader(
  lazy(() =>
    import(
      "../layouts/authentication/register/organisation/subscription/payment-success"
    )
  )
);

const PaymentFailed = ComponentLoader(
  lazy(() =>
    import(
      "../layouts/authentication/register/organisation/subscription/payment-failed"
    )
  )
);

const OrganisationRoutes = [
  {
    path: "",
    element: <AdminLayout />,
    children: [
      {
        path: "",
        index: true,
        element: <Admin />,
      },
      {
        path: "account",
        element: <Account />,
        children: [
          {
            path: "",
            index: true,
            element: <Profile />,
          },
          {
            path: "password",
            element: <UpdatePassword />,
          },
        ],
      },
      {
        path: "customers",
        element: <CardHolders />,
        children: [
          {
            path: "",
            index: true,
            element: <CardHoldersList />,
          },
          {
            path: "bulkmanage",
            element: <Bulkmanage />,
          },
          {
            path: "view-insight",
            element: <ViewInsight />,
          },
        ],
      },
      {
        path: "campaigns",
        children: [
          { index: true, element: <CampaignList /> },
          { path: "new", element: <CampaignStudio /> },
          { path: ":campaignId/edit", element: <CampaignStudio /> },
          { path: ":campaignId/insights", element: <CampaignInsights /> },
          { path: ":campaignId/members", element: <CampaignMembers /> },
        ],
      },
      {
        path: "scan",
        element: <ScanConsole />,
      },
      {
        path: "platform/campaigns",
        element: (
          <RequireRole roles={[ROLE.SUPER_ADMIN]} redirectTo="/campaigns">
            <SuperadminCampaigns />
          </RequireRole>
        ),
      },
      {
        path: "team",
        element: <Teams />,
        children: [
          {
            path: "",
            index: true,
            element: <Team />,
          },
          // {
          //   path: 'bulkmanage',
          //   element: <Bulkmanage />,
          // },
        ],
      },
      {
        path: "broadcast",
        element: <BroadCastModule />,
        children: [
          {
            path: "",
            index: true,
            element: <BroadCast />,
          },
        ],
      },
      {
        path: "help",
        element: <HelpVideos />,
      },
      {
        path: "design",
        element: <Designs />,
        children: [
          {
            path: "",
            index: true,
            element: <DesignList />,
          },
          {
            path: "create",
            element: <CreateDesign />,
          },
          {
            path: "update",
            element: <CreateDesign />,
          },
        ],
      },
      {
        path: "settings",
        element: <OrganisationSettings />,
      },
      {
        path: "settings/invoices",
        element: <InvoicesPage />,
      },
      {
        path: "rebranding",
        element: <Rebranding />,
      },
      {
        path: "order-management",
        element: <RestaurantModule />,
        children: restaurantChildren,
      },
      ...(UiKit ? [{ path: "ui-kit", element: <UiKit /> }] : []),

      {
        path: "*",
        element: <Navigate to={""} />,
      },
    ],
  },
  {
    path: "subscribe",
    children: [
      {
        path: "",
        element: <SubscriptionPlans />,
      },
      {
        path: "payment",
        element: <Payment />,
      },
      {
        path: "pay-by-card",
        element: <PayByCard />,
      },
      {
        path: "contact",
        element: <ContactSales />,
      },
      {
        path: "success",
        element: <Success />,
      },
      {
        path: "failed",
        element: <div>Failed</div>,
      },
    ],
  },
  {
    path: "payfast",
    element: <Payfast />,
  },
  {
    path: "payment-success",
    element: <PaymentSuccess />,
  },
  {
    path: "payment-failed",
    element: <PaymentFailed />,
  },
  {
    path: "*",
    element: <Navigate to={""} />,
  },
];

export default OrganisationRoutes;
